/**
 * PortalSMS — SMS Monetization & Compliance Dashboard
 *
 * Manage SMS campaigns, consent records, opt-outs, and delivery stats.
 * White-labeled — no provider branding visible.
 */

import React, { useState, useEffect, useCallback } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  MessageCircle, Plus, Loader2, Pencil, Save, ArrowLeft, Trash2,
  Pause, Play, Shield, Users, Send, Ban, CheckCircle, AlertTriangle,
} from 'lucide-react';
import { supabaseGet, supabasePost, supabasePatch, supabaseDelete } from '@/utils/supabaseRest';
import { useAuth } from '@/contexts/AuthContext';

interface SmsCampaign {
  id: string;
  name: string;
  description: string | null;
  message_template: string;
  send_type: string;
  trigger_event: string;
  trigger_delay_minutes: number;
  target_verticals: string[];
  status: string;
  total_sent: number;
  total_delivered: number;
  total_clicked: number;
  total_converted: number;
  total_opted_out: number;
  total_revenue: number;
  created_at: string;
}

interface SmsConsent {
  id: string;
  phone: string;
  status: string;
  consent_source: string;
  double_optin_confirmed: boolean;
  created_at: string;
}

type ActiveTab = 'campaigns' | 'consents' | 'optouts' | 'config';

export default function PortalSMS() {
  const { user } = useAuth();
  const [tab, setTab] = useState<ActiveTab>('campaigns');
  const [campaigns, setCampaigns] = useState<SmsCampaign[]>([]);
  const [consents, setConsents] = useState<SmsConsent[]>([]);
  const [optouts, setOptouts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);

  const [formName, setFormName] = useState('');
  const [formMessage, setFormMessage] = useState('');
  const [formTrigger, setFormTrigger] = useState('post_conversion');
  const [formDelay, setFormDelay] = useState(1440);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s, o] = await Promise.all([
        supabaseGet('sms_campaigns?order=created_at.desc'),
        supabaseGet('sms_consents?order=created_at.desc&limit=100'),
        supabaseGet('sms_optout_log?order=created_at.desc&limit=50'),
      ]);
      setCampaigns(c || []);
      setConsents(s || []);
      setOptouts(o || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!formName || !formMessage || !user?.id) return;
    setCreating(true);
    try {
      await supabasePost('sms_campaigns', {
        user_id: user.id, name: formName, message_template: formMessage,
        send_type: 'trigger', trigger_event: formTrigger,
        trigger_delay_minutes: formDelay, status: 'draft',
        include_opt_out_text: true,
      });
      setShowCreate(false); setFormName(''); setFormMessage(''); load();
    } catch (err) { console.error(err); }
    finally { setCreating(false); }
  };

  const toggle = async (c: SmsCampaign) => {
    const s = c.status === 'active' ? 'paused' : 'active';
    await supabasePatch(`sms_campaigns?id=eq.${c.id}`, { status: s }).catch(() => {});
    setCampaigns(prev => prev.map(x => x.id === c.id ? { ...x, status: s } : x));
  };

  const totalConsents = consents.filter(c => c.status === 'active').length;
  const totalOptedOut = consents.filter(c => c.status === 'opted_out').length;
  const totalSent = campaigns.reduce((s, c) => s + (c.total_sent || 0), 0);
  const totalRevenue = campaigns.reduce((s, c) => s + Number(c.total_revenue || 0), 0);

  return (
    <PortalLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <MessageCircle className="h-6 w-6 text-emerald-400" /> SMS Monetization
            </h1>
            <p className="text-sm text-slate-400 mt-1">TCPA-compliant SMS campaigns for post-conversion monetization.</p>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-4 text-center"><div className="text-2xl font-bold text-emerald-400">{totalConsents}</div><div className="text-xs text-slate-400">Active Consents</div></CardContent></Card>
          <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-4 text-center"><div className="text-2xl font-bold text-white">{totalSent}</div><div className="text-xs text-slate-400">Messages Sent</div></CardContent></Card>
          <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-4 text-center"><div className="text-2xl font-bold text-amber-400">${totalRevenue.toFixed(2)}</div><div className="text-xs text-slate-400">SMS Revenue</div></CardContent></Card>
          <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-4 text-center"><div className="text-2xl font-bold text-red-400">{totalOptedOut}</div><div className="text-xs text-slate-400">Opted Out</div></CardContent></Card>
          <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-4 text-center"><div className="text-2xl font-bold text-blue-400">{campaigns.length}</div><div className="text-xs text-slate-400">Campaigns</div></CardContent></Card>
        </div>

        {/* Compliance banner */}
        <Card className="bg-emerald-500/5 border-emerald-500/30">
          <CardContent className="p-3 flex items-center gap-3">
            <Shield className="h-5 w-5 text-emerald-400 shrink-0" />
            <div className="text-xs text-slate-400">
              <strong className="text-emerald-400">TCPA Compliant:</strong> All messages include opt-out language. Consent verified before every send. STOP keyword processing is automatic and immediate. Consent records are stored permanently for legal proof.
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex gap-2">
          {([['campaigns', 'Campaigns'], ['consents', 'Consent Records'], ['optouts', 'Opt-Out Log'], ['config', 'Provider Config']] as [ActiveTab, string][]).map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === k ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}>{l}</button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 text-emerald-400 animate-spin" /></div>
        ) : (
          <>
            {/* Campaigns */}
            {tab === 'campaigns' && (
              <div className="space-y-4">
                <div className="flex justify-end">
                  <Button onClick={() => setShowCreate(!showCreate)} className="bg-emerald-600 hover:bg-emerald-700"><Plus className="h-4 w-4 mr-1" /> New Campaign</Button>
                </div>

                {showCreate && (
                  <Card className="bg-slate-800/50 border-emerald-500/30">
                    <CardContent className="p-5 space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div><label className="text-xs text-slate-400 block mb-1">Campaign Name *</label><input value={formName} onChange={e => setFormName(e.target.value)} placeholder="Post-Conversion Follow-Up" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" /></div>
                        <div>
                          <label className="text-xs text-slate-400 block mb-1">Trigger</label>
                          <select value={formTrigger} onChange={e => setFormTrigger(e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                            <option value="post_conversion">After Conversion</option>
                            <option value="abandoned">Abandoned Session</option>
                            <option value="follow_up">Follow-Up (days later)</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">Message Template *</label>
                        <textarea value={formMessage} onChange={e => setFormMessage(e.target.value)} rows={3} placeholder="Hi {first_name}! You saved on {vertical}. Did you know you could save even more? Check this out: {offer_url}" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
                        <div className="text-[10px] text-slate-500 mt-1">Macros: {'{first_name}'}, {'{vertical}'}, {'{offer_url}'}, {'{state}'}, {'{payout}'}</div>
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">Delay (minutes after trigger)</label>
                        <input type="number" value={formDelay} onChange={e => setFormDelay(parseInt(e.target.value) || 0)} className="w-32 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
                        <span className="text-[10px] text-slate-500 ml-2">{formDelay >= 1440 ? `${Math.round(formDelay / 1440)} days` : formDelay >= 60 ? `${Math.round(formDelay / 60)} hours` : `${formDelay} min`}</span>
                      </div>
                      <div className="p-3 bg-slate-900 border border-slate-700 rounded-lg">
                        <div className="text-[10px] text-slate-500 mb-1">Preview (compliance footer auto-appended):</div>
                        <div className="text-sm text-white">{formMessage || 'Your message here...'}</div>
                        <div className="text-xs text-slate-500 mt-2 italic">Reply STOP to opt out. Msg&data rates may apply.</div>
                      </div>
                      <Button onClick={create} disabled={creating || !formName || !formMessage} className="bg-emerald-600 hover:bg-emerald-700">{creating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Create Campaign</Button>
                    </CardContent>
                  </Card>
                )}

                {campaigns.length === 0 ? (
                  <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-8 text-center"><MessageCircle className="h-10 w-10 text-slate-600 mx-auto mb-3" /><p className="text-sm text-slate-400">No SMS campaigns yet.</p></CardContent></Card>
                ) : (
                  <div className="space-y-2">
                    {campaigns.map(c => (
                      <Card key={c.id} className={`border ${c.status === 'active' ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-900/50 border-slate-800 opacity-60'}`}>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-white">{c.name}</span>
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${c.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : c.status === 'draft' ? 'bg-slate-600/20 text-slate-400' : 'bg-amber-500/20 text-amber-400'}`}>{c.status}</span>
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/20 text-blue-400">{c.trigger_event.replace(/_/g, ' ')}</span>
                              </div>
                              <div className="text-xs text-slate-500 mt-0.5 flex gap-3">
                                <span><Send className="h-3 w-3 inline mr-0.5" />{c.total_sent} sent</span>
                                <span><CheckCircle className="h-3 w-3 inline mr-0.5" />{c.total_delivered} delivered</span>
                                <span className="text-emerald-400">${Number(c.total_revenue || 0).toFixed(2)} revenue</span>
                                {c.total_opted_out > 0 && <span className="text-red-400"><Ban className="h-3 w-3 inline mr-0.5" />{c.total_opted_out} opt-outs</span>}
                              </div>
                            </div>
                            <button onClick={() => toggle(c)} className="px-2 py-1 rounded bg-slate-700 text-xs text-slate-300 hover:bg-slate-600">{c.status === 'active' ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}</button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Consent Records */}
            {tab === 'consents' && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-500 border-b border-slate-700">
                        <th className="text-left py-3 px-4">Phone</th>
                        <th className="text-center py-3 px-2">Status</th>
                        <th className="text-center py-3 px-2">Source</th>
                        <th className="text-center py-3 px-2">Double Opt-In</th>
                        <th className="text-right py-3 px-4">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {consents.length === 0 ? (
                        <tr><td colSpan={5} className="py-8 text-center text-slate-500">No consent records yet. Consents are captured when users opt-in via the widget.</td></tr>
                      ) : consents.map(c => (
                        <tr key={c.id} className="border-b border-slate-800">
                          <td className="py-2 px-4 text-white font-mono text-xs">{c.phone}</td>
                          <td className="py-2 px-2 text-center">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${c.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>{c.status}</span>
                          </td>
                          <td className="py-2 px-2 text-center text-xs text-slate-400">{c.consent_source}</td>
                          <td className="py-2 px-2 text-center">{c.double_optin_confirmed ? <CheckCircle className="h-4 w-4 text-emerald-400 inline" /> : <AlertTriangle className="h-4 w-4 text-amber-400 inline" />}</td>
                          <td className="py-2 px-4 text-right text-xs text-slate-500">{new Date(c.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* Opt-Out Log */}
            {tab === 'optouts' && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-500 border-b border-slate-700">
                        <th className="text-left py-3 px-4">Phone</th>
                        <th className="text-left py-3 px-2">Keyword</th>
                        <th className="text-center py-3 px-2">Processed</th>
                        <th className="text-right py-3 px-4">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {optouts.length === 0 ? (
                        <tr><td colSpan={4} className="py-8 text-center text-slate-500">No opt-outs recorded.</td></tr>
                      ) : optouts.map((o: any) => (
                        <tr key={o.id} className="border-b border-slate-800">
                          <td className="py-2 px-4 text-white font-mono text-xs">{o.phone}</td>
                          <td className="py-2 px-2 text-xs text-red-400">{o.keyword}</td>
                          <td className="py-2 px-2 text-center">{o.processed ? <CheckCircle className="h-4 w-4 text-emerald-400 inline" /> : <AlertTriangle className="h-4 w-4 text-red-400 inline" />}</td>
                          <td className="py-2 px-4 text-right text-xs text-slate-500">{new Date(o.created_at).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* Provider Config */}
            {tab === 'config' && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-5 space-y-4">
                  <p className="text-sm text-slate-400">Configure your SMS provider credentials. These are stored securely and never exposed to end users.</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs text-slate-400 block mb-1">API Key</label><input type="password" placeholder="Enter your API key" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" /></div>
                    <div><label className="text-xs text-slate-400 block mb-1">API Secret (optional)</label><input type="password" placeholder="Enter API secret" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" /></div>
                    <div><label className="text-xs text-slate-400 block mb-1">From Number</label><input placeholder="+18005551234" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" /></div>
                    <div><label className="text-xs text-slate-400 block mb-1">Daily Limit</label><input type="number" placeholder="10000" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" /></div>
                  </div>
                  <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-xs text-amber-400">
                    <strong>10DLC Registration Required:</strong> Before sending SMS at scale, you must register your brand and campaign with carriers. Contact your SMS provider to complete 10DLC registration.
                  </div>
                  <Button className="bg-emerald-600 hover:bg-emerald-700"><Save className="h-4 w-4 mr-1" /> Save Configuration</Button>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </PortalLayout>
  );
}
